import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { GroupService } from '@thiememeulenhoff/ng-api-gateway';
import { User } from '@thiememeulenhoff/types';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';

type UsersState = {
  users: Array<User>;
  currentUserIdentifier: string | null;
};

const initialState: UsersState = {
  users: [],
  currentUserIdentifier: null,
};

export const UsersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ users, currentUserIdentifier }) => ({
    currentUser: computed(() =>
      users().find((user) => user.tmProfileId === currentUserIdentifier()),
    ),
  })),
  withRequestStatus(),
  withMethods((store, groupService = inject(GroupService)) => ({
    fetchCurrentUser: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { requestStatus: 'loading' })),
        switchMap(() =>
          groupService.fetchCurrentUser().pipe(
            tapResponse({
              next: (user) =>
                patchState(store, {
                  users: [user],
                  currentUserIdentifier: user.tmProfileId,
                  requestStatus: 'success',
                }),
              error: (error: unknown) =>
                patchState(store, {
                  requestStatus: toRequestError(error, 'Failed to fetch users'),
                  currentUserIdentifier: null,
                }),
            }),
          ),
        ),
      ),
    ),
    fetchUsersByProfileIds: rxMethod<Array<string>>(
      pipe(
        tap(() => patchState(store, { requestStatus: 'loading' })),
        switchMap((tmProfileIds) =>
          groupService.fetchUsersByProfileIds(tmProfileIds).pipe(
            tapResponse({
              next: (users) =>
                patchState(store, { users, requestStatus: 'success' }),
              error: (error: unknown) =>
                patchState(store, {
                  requestStatus: toRequestError(error, 'Failed to fetch users'),
                }),
            }),
          ),
        ),
      ),
    ),
  })),
);
